import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { knowledgeBaseApi } from '../../services/api/knowledgeBaseApi';
import type { KBSuggestion } from '../../utils/suggestionToLineItem';

export type { KBSuggestion };

export interface KnowledgeBaseSuggestionsProps {
  vehicle: { year?: number; make?: string; model?: string } | null;
  onAdd: (suggestion: KBSuggestion) => void;
}

const KnowledgeBaseSuggestions: React.FC<KnowledgeBaseSuggestionsProps> = ({ vehicle, onAdd }) => {
  const [suggestions, setSuggestions] = useState<KBSuggestion[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!vehicle?.year || !vehicle?.make || !vehicle?.model) {
      setSuggestions([]);
      return;
    }
    setDismissed(false);
    knowledgeBaseApi
      .suggest(vehicle.year, vehicle.make, vehicle.model)
      .then(setSuggestions)
      .catch(() => setSuggestions([]));
  }, [vehicle?.year, vehicle?.make, vehicle?.model]);

  if (dismissed || suggestions.length === 0) return null;

  return (
    <Alert
      type="info"
      message={`Previous work for ${vehicle?.year} ${vehicle?.make} ${vehicle?.model}`}
      onClose={() => setDismissed(true)}
      closable
      description={
        <Space direction="vertical" style={{ width: '100%' }}>
          {suggestions.slice(0, 5).map((s) => (
            <Space key={s.description} wrap>
              <Tag>{s.count}x</Tag>
              <span>{s.description}</span>
              <span style={{ color: '#666' }}>
                {s.avg_labor_hours.toFixed(1)}h &middot; ${s.avg_total.toFixed(0)}
              </span>
              {s.parts && s.parts.length > 0 && (
                <Tag color="blue">+{s.parts.length} part{s.parts.length > 1 ? 's' : ''}</Tag>
              )}
              {s.last_notes && <Tag color="gold">note</Tag>}
              <Button
                size="small"
                type="link"
                icon={<PlusOutlined />}
                onClick={() => onAdd(s)}
              >
                Add
              </Button>
            </Space>
          ))}
        </Space>
      }
    />
  );
};

export default KnowledgeBaseSuggestions;
